import { Box, CircularProgress, Fab, Stack } from '@mui/material';
import { CheckCircleOutline, HighlightOffOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useForm, type SubmitHandler } from 'react-hook-form';

import { FormAutocomplete } from '@/shared/ui/form-components/FormAutocomplete';
import { FormCheckbox } from '@/shared/ui/form-components/FormCheckbox';
import { FormTextField } from '@/shared/ui/form-components/FormTextField';
import type { MinimalCustomer, RoleDtoRole } from '@/generated/model';

import type { UserAccessFormDirtyFields, UserAccessFormValues } from '../../types/UserAccessFormValues';
import { getRoleLabel } from '../../utils/roleUtils';
import { cancelButtonSx, saveButtonSx } from './UserAccessForm.styles';

type UserAccessFormSubmitHandler = (
  data: UserAccessFormValues,
  dirtyFields: UserAccessFormDirtyFields
) => Promise<void> | void;

type UserAccessFormMode = 'create' | 'edit' | 'self-service';

type UserAccessFormFieldRules = {
  isSurnameReadOnly: boolean;
  isFirstNameReadOnly: boolean;
  isUsernameReadOnly: boolean;
  isRoleReadOnly: boolean;
  isActiveVisible: boolean;
  isActiveDisabled: boolean;
  isCustomerReadOnly: boolean;
};

const fieldRulesByMode = {
  create: {
    isSurnameReadOnly: false,
    isFirstNameReadOnly: false,
    isUsernameReadOnly: false,
    isRoleReadOnly: false,
    isActiveVisible: false,
    isActiveDisabled: false,
    isCustomerReadOnly: false,
  },
  edit: {
    isSurnameReadOnly: false,
    isFirstNameReadOnly: false,
    isUsernameReadOnly: true,
    isRoleReadOnly: false,
    isActiveVisible: true,
    isActiveDisabled: false,
    isCustomerReadOnly: false,
  },
  'self-service': {
    isSurnameReadOnly: true,
    isFirstNameReadOnly: true,
    isUsernameReadOnly: true,
    isRoleReadOnly: true,
    isActiveVisible: true,
    isActiveDisabled: true,
    isCustomerReadOnly: true,
  },
} satisfies Record<UserAccessFormMode, UserAccessFormFieldRules>;

type UserAccessFormProps = {
  mode: UserAccessFormMode;
  initialValues: UserAccessFormValues;
  customers: MinimalCustomer[];
  roles: RoleDtoRole[];
  isPending?: boolean;
  onCancel: () => void;
  onSubmit: UserAccessFormSubmitHandler;
};

export function UserAccessForm({
  mode,
  initialValues,
  customers,
  roles,
  isPending = false,
  onCancel,
  onSubmit,
}: UserAccessFormProps) {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    formState: { dirtyFields, isDirty },
  } = useForm<UserAccessFormValues>({ defaultValues: initialValues });
  const fieldRules = fieldRulesByMode[mode];

  const submitForm: SubmitHandler<UserAccessFormValues> = async (data) => {
    await onSubmit(data, dirtyFields);
  };
  const requiredMessage = t('userAccessControl.errors.required');

  return (
    <Box component="form" onSubmit={handleSubmit(submitForm)} noValidate p={1}>
      <Stack width="100%" gap={1}>
        <FormTextField
          name="surname"
          control={control}
          label={t('userAccessControl.fields.surname')}
          required
          requiredMessage={requiredMessage}
          slotProps={{ htmlInput: { readOnly: fieldRules.isSurnameReadOnly } }}
        />
        <FormTextField
          name="firstName"
          control={control}
          label={t('userAccessControl.fields.firstName')}
          required
          requiredMessage={requiredMessage}
          slotProps={{ htmlInput: { readOnly: fieldRules.isFirstNameReadOnly } }}
        />
        <FormTextField
          name="username"
          control={control}
          label={t('userAccessControl.fields.username')}
          required
          requiredMessage={requiredMessage}
          slotProps={{ htmlInput: { readOnly: fieldRules.isUsernameReadOnly } }}
        />
        <FormTextField name="phone" control={control} label={t('userAccessControl.fields.phone')} />
        <FormAutocomplete
          name="role"
          control={control}
          label={t('userAccessControl.fields.role')}
          options={roles}
          getOptionLabel={(role) => getRoleLabel(t, role)}
          required
          requiredMessage={requiredMessage}
          readOnly={fieldRules.isRoleReadOnly}
        />
        {fieldRules.isActiveVisible && (
          <FormCheckbox
            name="active"
            control={control}
            label={t('userAccessControl.fields.active')}
            disabled={fieldRules.isActiveDisabled}
          />
        )}
        <FormAutocomplete
          name="customer"
          control={control}
          label={t('userAccessControl.fields.customer')}
          options={customers}
          getOptionLabel={(customer) => customer.name}
          getOptionKey={(customer) => customer.id}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          required
          requiredMessage={requiredMessage}
          readOnly={fieldRules.isCustomerReadOnly}
        />
      </Stack>
      <Stack spacing={1} alignItems="flex-end" mt={2}>
        <Fab
          type="submit"
          variant="extended"
          disabled={isPending || !isDirty}
          aria-label={t('userAccessControl.actions.save')}
          data-qa-element-id="user_access_control_save_button"
          sx={saveButtonSx}
        >
          {isPending ? <CircularProgress size={20} /> : <CheckCircleOutline sx={{ mr: 1 }} />}
          {t('userAccessControl.actions.save')}
        </Fab>
        <Fab
          type="button"
          variant="extended"
          onClick={onCancel}
          aria-label={t('userAccessControl.actions.cancel')}
          data-qa-element-id="user_access_control_cancel_button"
          sx={cancelButtonSx}
        >
          <HighlightOffOutlined sx={{ mr: 1 }} />
          {t('userAccessControl.actions.cancel')}
        </Fab>
      </Stack>
    </Box>
  );
}
